import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Tooltip,
  CardHeader,
  useTheme,
  CircularProgress,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm, Controller } from 'react-hook-form';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { useModal } from '../../../../components/common';
import { useAuth } from '../../../../components/auth/AuthProvider';
import {
  useDeleteMigrationRecord,
  useGetMigrationRecord,
  useUpdateMigrationRecord,
} from '../../../../hooks';
import { MigrationEvent } from '../../../../types/migration';
import queryClient from '../../../../core/http/react-query';
import DeleteModal from '../../../../components/common/DeleteModal';
import ErrorDisplay from '../../../../components/common/ErrorDisplay';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../../utils';
import { textLimits } from '../../../../core/constants/constants';

type recordDetailsProps = {
  recordId: string;
  treeId?: string;
  onDelete?: () => void;
};

type TimelineEntry = {
  event: MigrationEvent;
  sourceIndex: number;
};

export default function RecordDetails({
  recordId,
  treeId,
  onDelete,
}: recordDetailsProps) {
  const { openModal, closeModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { palette } = theme;
  const isMobileTimeline = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MigrationEvent & { member_ids: string[] }>({
    defaultValues: {
      year: 0,
      event: '',
      location: '',
      latitude: 0,
      longitude: 0,
    },
  });

  const [isEditing, setIsEditing] = useState(false);

  const { currentUser } = useAuth();

  const { data, isLoading, isError, error, refetch } =
    useGetMigrationRecord(recordId);

  const updateMutation = useUpdateMigrationRecord(recordId);
  const deleteMutation = useDeleteMigrationRecord(recordId);

  const migrationRecord = useMemo(() => {
    if (data) {
      return data as any;
    }
    return null;
  }, [data]);

  useEffect(() => {
    if (!isLoading && !isError && migrationRecord === null) {
      onDelete?.();
    }
  }, [isLoading, isError, migrationRecord, onDelete]);

  const timelineEntries = useMemo<TimelineEntry[]>(() => {
    if (!migrationRecord?.timeline) {
      return [];
    }

    return (migrationRecord.timeline as MigrationEvent[])
      .map(
        (event: MigrationEvent, sourceIndex: number): TimelineEntry => ({
          event,
          sourceIndex,
        }),
      )
      .sort(
        (a: TimelineEntry, b: TimelineEntry) => a.event.year - b.event.year,
      );
  }, [migrationRecord]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  if (!migrationRecord) {
    return (
      <Box p={2}>
        <Typography variant="h6">
          Add your family journey to see details here.
        </Typography>
      </Box>
    );
  }
  const handleAddEvent = (data: any) => {
    if (!currentUser) return;

    const updatedRecord = {
      ...migrationRecord,
      updated_by: currentUser.uid,
      member_ids: migrationRecord.member_ids ?? [],
      timeline: [
        ...migrationRecord.timeline,
        {
          year: parseInt(data.year),
          event: sanitiseTextInput(data.event),
          location: sanitiseNameInput(data.location || ''),
          latitude: data.latitude ? parseFloat(data.latitude) : null,
          longitude: data.longitude ? parseFloat(data.longitude) : null,
        },
      ].sort((a, b) => a.year - b.year),
    };

    updateMutation.mutate(updatedRecord, {
      onSuccess: () => {
        enqueueSnackbar('Migration record updated successfully!', {
          variant: 'success',
        });
        setIsEditing(false);
        reset();
        queryClient.refetchQueries({
          queryKey: ['migrationRecords', recordId],
          exact: true,
        });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to update migration record', {
          variant: 'error',
        });
        console.error('Error updating migration record:', error);
      },
    });
  };

  const handleRemoveEvent = (index: number) => {
    if (!currentUser) return;

    const updatedTimeline = [...migrationRecord.timeline];
    updatedTimeline.splice(index, 1);

    const updatedRecord = {
      ...migrationRecord,
      updated_by: currentUser.uid,
      member_ids: migrationRecord.member_ids ?? [],
      timeline: updatedTimeline,
    };

    updateMutation.mutate(updatedRecord, {
      onSuccess: () => {
        enqueueSnackbar('Migration record updated successfully!', {
          variant: 'success',
        });
        setIsEditing(false);
        reset();
        queryClient.refetchQueries({
          queryKey: ['migrationRecords', recordId],
          exact: true,
        });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to update migration record', {
          variant: 'error',
        });
        console.error('Error updating migration record:', error);
      },
    });
  };

  function handleDeleteRecord() {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar('Migration record deleted successfully!', {
          variant: 'success',
        });
        reset();
        if (treeId) {
          queryClient.refetchQueries({
            queryKey: ['migrationRecords', treeId],
            exact: true,
          });
        }
        if (currentUser?.uid) {
          queryClient.refetchQueries({
            queryKey: ['migrationRecords', currentUser.uid],
            exact: true,
          });
        }
        queryClient.removeQueries({
          queryKey: ['migrationRecords', recordId],
          exact: true,
        });
        closeModal?.();
        onDelete?.();
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete migration record', {
          variant: 'error',
        });
        console.error('Error deleting migration record:', error);
      },
    });
  }

  const TimeLineView = () => (
    <Box
      sx={{
        py: 1,
        maxHeight: { xs: 'none', md: '68vh' },
        overflowY: { xs: 'visible', md: 'auto' },
        pr: { xs: 0, md: 1 },
      }}
    >
      <Timeline
        sx={{
          m: 0,
          p: 0,
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
          '& .MuiTimelineConnector-root': {
            bgcolor: 'divider',
          },
        }}
      >
        {timelineEntries.map(
          ({ event, sourceIndex }: TimelineEntry, index: number) => (
            <TimelineItem
              key={`timeline_${index}`}
              position={isMobileTimeline ? 'right' : 'alternate'}
              sx={{
                animation: 'timelineItemFade 280ms cubic-bezier(0.2, 0, 0, 1)',
                animationDelay: `${index * 40}ms`,
                animationFillMode: 'both',
                '@keyframes timelineItemFade': {
                  '0%': { opacity: 0, transform: 'translateY(6px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <TimelineOppositeContent
                sx={{
                  m: 'auto 0',
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
                align="right"
                variant="h6"
                color="text.secondary"
              >
                {event.year}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot
                  color="primary"
                  sx={{
                    boxShadow: '0px 0px 0px 4px rgba(25, 118, 210, 0.16)',
                    p: 0.7,
                  }}
                >
                  <PersonPinCircleOutlinedIcon fontSize="small" />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: 1.5, px: 2 }}>
                <Card
                  sx={{
                    minWidth: { xs: '100%', sm: 275 },
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0px 6px 18px rgba(16, 24, 40, 0.08)',
                    transition:
                      'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 10px 24px rgba(16, 24, 40, 0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Chip label={event.year} color="info" size="small" />
                      {isEditing && (
                        <Tooltip title="Delete event">
                          <IconButton
                            onClick={() => handleRemoveEvent(sourceIndex)}
                            size="small"
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                            }}
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      py={2}
                      color="text.secondary"
                      lineHeight={1.8}
                    >
                      {event.event}
                    </Typography>
                    {event.location && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          py: 0.5,
                          px: 1.25,
                          borderRadius: 2,
                          bgcolor: 'action.hover',
                          mb: 0.75,
                        }}
                      >
                        <FmdGoodOutlinedIcon fontSize="small" color="primary" />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {event.location}
                        </Typography>
                      </Box>
                    )}
                    {event.latitude && event.longitude && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          py: 0.25,
                        }}
                      >
                        <PlaceIcon fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Latitude: {event.latitude} | Longitude:{' '}
                          {event.longitude}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          ),
        )}
      </Timeline>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          component="h2"
          variant="h6"
          sx={{
            mb: 2,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            maxWidth: '100%',
          }}
        >
          {migrationRecord?.title}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {!isEditing && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setIsEditing(!isEditing)}
                sx={{
                  minHeight: 36,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Add timeline
              </Button>
              <Tooltip title="Delete journey">
                <IconButton
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                  onClick={() => {
                    openModal(
                      <DeleteModal
                        closeModal={closeModal}
                        onDelete={handleDeleteRecord}
                        deleteMessage={`Are you sure you want to delete migration record: ${migrationRecord?.title}?`}
                        deleteTitle={'Delete migration record'}
                      />,
                    );
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
      <Typography
        component="p"
        variant="body1"
        sx={{
          my: 2,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          maxWidth: '100%',
        }}
      >
        {migrationRecord?.description}
      </Typography>
      <Box sx={{ p: { xs: '0' } }}>
        {isEditing && (
          <Card
            sx={{
              my: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0px 8px 20px rgba(16, 24, 40, 0.08)',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              animation: 'timelineEditFade 220ms cubic-bezier(0.2, 0, 0, 1)',
              '@keyframes timelineEditFade': {
                '0%': { opacity: 0, transform: 'translateY(5px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <CardHeader title="Add migration event" />
            <CardContent>
              <Box component="form" onSubmit={handleSubmit(handleAddEvent)}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="year"
                      control={control}
                      rules={{
                        required: 'Year is required',
                        pattern: {
                          value: /^\d+$/,
                          message: 'Year must be a number',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Year"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          variant="outlined"
                          error={!!errors.year}
                          helperText={errors.year?.message}
                          placeholder="e.g. 2008"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Location"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          variant="outlined"
                          error={!!errors.location}
                          helperText={errors.location?.message}
                          placeholder="e.g. Ellis Island, New York"
                          size="small"
                          inputProps={{
                            maxLength: textLimits.migrationLocation,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="latitude"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^-?[0-9]\d*(\.\d+)?$/,
                          message: 'Must be a valid latitude',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Latitude"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          variant="outlined"
                          error={!!errors.latitude}
                          helperText={errors.latitude?.message}
                          placeholder="e.g. 40.6992"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="longitude"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^-?[0-9]\d*(\.\d+)?$/,
                          message: 'Must be a valid longitude',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Longitude"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          variant="outlined"
                          error={!!errors.longitude}
                          helperText={errors.longitude?.message}
                          placeholder="e.g. -74.0410"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="event"
                      control={control}
                      rules={{
                        required: 'Event description is required',
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Event Description"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          error={
                            !!errors.event ||
                            hasReachedCharacterLimit(
                              field.value,
                              textLimits.migrationEvent,
                            )
                          }
                          helperText={
                            errors.event?.message ??
                            getCharacterCountHelperText(
                              field.value,
                              textLimits.migrationEvent,
                            )
                          }
                          placeholder="Describe the migration event"
                          size="small"
                          inputProps={{ maxLength: textLimits.migrationEvent }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                        gap: 2,
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setIsEditing(false)}
                        sx={{
                          minHeight: 36,
                          px: 2,
                          borderRadius: 2,
                          textTransform: 'none',
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{
                          minHeight: 36,
                          px: 2,
                          borderRadius: 2,
                          textTransform: 'none',
                        }}
                      >
                        Add migration event
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {timelineEntries.length === 0 ? (
          !isEditing ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              p={4}
              borderRadius={4}
              bgcolor="background.paper"
              boxShadow={1}
              maxWidth={400}
              mx="auto"
              my={8}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={72}
                height={72}
                mb={3}
                borderRadius="50%"
                bgcolor={palette.grey[100]}
              >
                <HourglassEmptyIcon
                  sx={{ fontSize: 40, color: 'primary.main' }}
                />
              </Box>

              <Typography variant="h6" gutterBottom color="text.primary">
                No timeline available
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={3}>
                You can add timeline to get started.
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => setIsEditing(!isEditing)}
                sx={{
                  minHeight: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Add timeline
              </Button>
            </Box>
          ) : null
        ) : (
          <TimeLineView />
        )}
      </Box>
    </Box>
  );
}
